<script lang="ts" setup>
import { ref, computed } from 'vue'

interface Props {
  tooltipContent?: string
  helpTextContent?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
}

const props = withDefaults(defineProps<Props>(), {
  tooltipContent: '',
  helpTextContent: '',
  position: 'top',
  className: ''
})

const isVisible = ref(false)

const tooltipId = `tooltip-${Math.random().toString(36).substring(2, 9)}`
const hasContent = computed(() => props.tooltipContent.trim() || props.helpTextContent.trim())

const positionClasses = {
  top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-3',
  bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-3',
  left: 'right-full top-1/2 transform -translate-y-1/2 mr-3',
  right: 'left-full top-1/2 transform -translate-y-1/2 ml-3'
}

const arrowClasses = {
  top: 'top-full left-1/2 transform -translate-x-1/2 border-t-slate-800 border-t-[8px] border-l-transparent border-r-transparent border-l-[8px] border-r-[8px]',
  bottom: 'bottom-full left-1/2 transform -translate-x-1/2 border-b-slate-800 border-b-[8px] border-l-transparent border-r-transparent border-l-[8px] border-r-[8px]',
  left: 'left-full top-1/2 transform -translate-y-1/2 border-l-slate-800 border-l-[8px] border-t-transparent border-b-transparent border-t-[8px] border-b-[8px]',
  right: 'right-full top-1/2 transform -translate-y-1/2 border-r-slate-800 border-r-[8px] border-t-transparent border-b-transparent border-t-[8px] border-b-[8px]'
}
</script>

<template>
  <div
    v-if="hasContent"
    class="relative inline-block"
    :class="props.className"
    @mouseenter="isVisible = true"
    @mouseleave="isVisible = false"
    @focus="isVisible = true"
    @blur="isVisible = false"
  >
    <slot />

    <Transition name="tooltip-fade">
      <div
        v-if="isVisible"
        :id="tooltipId"
        role="tooltip"
        :class="[
          'absolute z-50 px-4 py-3 text-sm font-medium text-white bg-slate-500 rounded-xl shadow-2xl max-w-2xl min-w-80 backdrop-blur-sm border border-slate-700',
          positionClasses[props.position]
        ]"
        :style="{
          lineHeight: '1.5',
          wordWrap: 'break-word',
          hyphens: 'auto'
        }"
      >
        <div class="relative space-y-2">
          <ul class="list-disc list-inside space-y-1">
            <li v-if="props.tooltipContent">{{ props.tooltipContent }}</li>
            <li v-if="props.helpTextContent">{{ props.helpTextContent }}</li>
          </ul>
        </div>
        <div class="absolute w-0 h-0" :class="arrowClasses[props.position]" />
      </div>
    </Transition>
  </div>

  <!-- Si aucun contenu de tooltip, on retourne juste le slot -->
  <template v-else>
    <slot />
  </template>
</template>

<style scoped>
.tooltip-fade-enter-active {
  animation: tooltipFadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
